'use client';
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import InboxIcon from '~/assets/icons/ontario-icon-notification.svg';
import OntarioAccountIcon from '~/assets/icons/ontario-icon-account.svg';
import MailIcon from '~/assets/icons/ontario-icon-email.svg';
import AccountIcon from '~/assets/icons/ontario-icon-account.svg';
import DocumentIcon from '~/assets/icons/ontario-icon-document.svg';
import ExitIcon from '~/assets/icons/ontario-icon-exit.svg';
import UnreadMarker from '~/assets/icons/unread-marker.svg';
import CustomPopup from '~/components/common/popup';
import {
    SubHeaderContainer,
    MenuButton,
    MenuList,
    MenuListItem,
    MenuItemLink,
    AppHeaderContainer,
    ButtonContainer,
    InboxButton,
    InboxList,
    InboxListItem,
    InboxListItemContainer,
    MessageContainer,
    MessageSubject,
    MessageBody,
    MessageDate,
    UnreadCounter,
    StyledMessageLink,
    InboxHeadingContainer,
    StyledName,
    SubHeaderDescription,
    SubHeaderHeading,
    AppHeaderInnerContainer,
} from '~/components/common/application-header/styles';
import { cxpSignOut } from '~/utils/auth';
import { Session } from '~/models/auth';
import { formatMessageDate, shouldShowMenuButton } from '~/utils/helpers';
import { storeContext } from '~/store/store';
import colors from '~/theme/variables/colors';
import { TextParagraph, TextParagraphBold } from '~/theme/elements';
import { APP_HEADER_MAX_INBOX_MESSAGES } from '~/constants';
import sanitizeHtml from '~/utils/sanitize-html';

const menuOptions = [
    {
        id: 'myInbox',
        title: 'appBar.menuOptionMyInbox',
        link: '/notifications/inbox',
        icon: <MailIcon aria-hidden="true" focusable="false" />,
    },
    {
        id: 'myAccount',
        title: 'appBar.menuOptionMyAccount',
        link: '/profile/details',
        icon: <AccountIcon aria-hidden="true" focusable="false" />,
    },
    {
        id: 'myTransactions',
        title: 'appBar.menuOptionTransaction',
        link: '/transactions',
        icon: <DocumentIcon aria-hidden="true" focusable="false" />,
    },
];

interface ApplicationHeaderProps {
    customAppTitle?: string;
    hideAccountActions?: boolean;
}

export default function ApplicationHeader({
    customAppTitle,
    hideAccountActions = false,
}: ApplicationHeaderProps) {
    const { data: session } = useSession();
    const [isMenuActive, setIsMenuActive] = useState(false);
    const [isInboxActive, setIsInboxActive] = useState(false);
    const { state } = useContext(storeContext);
    const { t } = useTranslation('base-layout');
    const router = useRouter();
    const unreadMessagesCount = state?.inbox?.messages?.filter(message => !message.isRead).length;

    const handleSignOutClick = () => {
        cxpSignOut((session as Session)?.idToken);
    };

    const handleMenuItemClick = (link: string) => () => {
        if (!link) {
            return;
        }
        setIsMenuActive(false);
        setIsInboxActive(false);
        router.push(link);
    };

    const hasDashboardAccess =
        (session as Session)?.user?.id &&
        shouldShowMenuButton(router.pathname) &&
        !hideAccountActions;

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') setIsMenuActive(false);
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return (
        <AppHeaderContainer>
            <AppHeaderInnerContainer role="navigation">
                <SubHeaderContainer>
                    <SubHeaderHeading>{t('appBar.appBarTitle')}</SubHeaderHeading>
                    {!!customAppTitle && (
                        <SubHeaderDescription>{customAppTitle}</SubHeaderDescription>
                    )}
                </SubHeaderContainer>
                {hasDashboardAccess && (
                    <ButtonContainer>
                        <InboxButton
                            onClick={() => {
                                setIsInboxActive(!isInboxActive);
                                setIsMenuActive(false);
                            }}
                            aria-expanded={!!isInboxActive}
                            aria-controls="inboxPopup"
                            aria-label={
                                unreadMessagesCount
                                    ? `Inbox, ${unreadMessagesCount} unread messages`
                                    : 'Inbox'
                            }
                        >
                            <InboxIcon
                                aria-hidden="true"
                                focusable="false"
                                color={colors.palette.white}
                                className="ontario-icon"
                            />
                        </InboxButton>
                        {unreadMessagesCount !== 0 && (
                            <UnreadCounter>{unreadMessagesCount}</UnreadCounter>
                        )}
                        {isInboxActive && (
                            <CustomPopup
                                id="inboxPopup"
                                onClickOutside={() => {
                                    setIsInboxActive(false);
                                }}
                                isVisible={isInboxActive}
                            >
                                <InboxList>
                                    <InboxHeadingContainer>
                                        <TextParagraphBold>
                                            {t('appBar.inboxHeading')}
                                        </TextParagraphBold>
                                        {(!state?.inbox?.messages?.length ||
                                            unreadMessagesCount === 0) && (
                                            <TextParagraph aria-live="polite">
                                                {t('appBar.noNewMessages')}
                                            </TextParagraph>
                                        )}
                                    </InboxHeadingContainer>
                                    {!!unreadMessagesCount &&
                                        unreadMessagesCount > 0 &&
                                        state?.inbox?.messages
                                            ?.sort(
                                                (a, b) =>
                                                    Date.parse(b.messageDateTime) -
                                                    Date.parse(a.messageDateTime)
                                            )
                                            .slice(0, APP_HEADER_MAX_INBOX_MESSAGES)
                                            .map(message => (
                                                <StyledMessageLink
                                                    href={`/notifications/message?messageId=${message.messageId}`}
                                                    key={`${message.messageId}messageLink`}
                                                >
                                                    <InboxListItem
                                                        isRead={message.isRead}
                                                        key={`${message.messageId}messageli`}
                                                    >
                                                        <InboxListItemContainer
                                                            isRead={message.isRead}
                                                            key={`${message.messageId}liContainer`}
                                                        >
                                                            {!message.isRead && (
                                                                <UnreadMarker
                                                                    aria-hidden="true"
                                                                    focusable="false"
                                                                    key={`${message.messageId}unreadMarker`}
                                                                />
                                                            )}
                                                            <MessageContainer
                                                                isRead={message.isRead}
                                                                key={`${message.messageId}messagecontainer`}
                                                            >
                                                                <MessageSubject
                                                                    isRead={message.isRead}
                                                                    key={`${message.messageId}messageSubject`}
                                                                >
                                                                    {message.subject}
                                                                </MessageSubject>
                                                                <MessageBody
                                                                    key={`${message.messageId}messageBody`}
                                                                >
                                                                    {sanitizeHtml(
                                                                        message.body ?? '',
                                                                        {
                                                                            FORCE_BODY: true,
                                                                            ALLOWED_TAGS: ['a'],
                                                                        }
                                                                    )}
                                                                </MessageBody>
                                                                <MessageDate
                                                                    key={`${message.messageId}messageDate`}
                                                                >
                                                                    {formatMessageDate(
                                                                        message.messageDateTime
                                                                    )}
                                                                </MessageDate>
                                                            </MessageContainer>
                                                        </InboxListItemContainer>
                                                    </InboxListItem>
                                                </StyledMessageLink>
                                            ))}
                                    {state?.inbox?.messages?.length !== 0 &&
                                        unreadMessagesCount !== 0 && (
                                            <InboxListItem>
                                                <Link href="/notifications/inbox">
                                                    {t('appBar.viewMyInbox')}
                                                </Link>
                                            </InboxListItem>
                                        )}
                                </InboxList>
                            </CustomPopup>
                        )}
                        <MenuButton
                            onClick={() => {
                                setIsMenuActive(!isMenuActive);
                                setIsInboxActive(false);
                            }}
                            aria-expanded={!!isMenuActive}
                            aria-controls="menuPopup"
                            aria-label="Menu"
                        >
                            <OntarioAccountIcon
                                aria-hidden="true"
                                focusable="false"
                                className="ontario-icon"
                            />
                            <StyledName>{state?.customer?.details?.firstName}</StyledName>
                        </MenuButton>
                        {isMenuActive && (
                            <CustomPopup
                                id="menuPopup"
                                onClickOutside={() => {
                                    setIsMenuActive(false);
                                }}
                                isVisible={isMenuActive}
                            >
                                <MenuList>
                                    {menuOptions.map(menuItem => (
                                        <MenuListItem
                                            key={menuItem.id}
                                            onClick={handleMenuItemClick(menuItem.link)}
                                        >
                                            {menuItem.icon}
                                            <MenuItemLink
                                                href={menuItem.link}
                                                onClick={e => e.preventDefault()}
                                            >
                                                {t(menuItem.title)}
                                            </MenuItemLink>
                                        </MenuListItem>
                                    ))}
                                    <MenuListItem onClick={handleSignOutClick}>
                                        <ExitIcon aria-hidden="true" focusable="false" />
                                        <MenuItemLink onClick={handleSignOutClick}>
                                            {t('appBar.menuOptionSignOut')}
                                        </MenuItemLink>
                                    </MenuListItem>
                                </MenuList>
                            </CustomPopup>
                        )}
                    </ButtonContainer>
                )}
            </AppHeaderInnerContainer>
        </AppHeaderContainer>
    );
}
